/**
 * Quotes List Component
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText, Mail, Phone, MapPin } from "lucide-react";
import { authAxios } from "@/utils/auth";

const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
};

export default function QuotesList() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchQuotes = useCallback(async () => {
    try {
      const res = await authAxios.get("/quotes");
      setQuotes(res.data);
    } catch {
      toast.error("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const updateStatus = useCallback(async (quoteId, status) => {
    try {
      await authAxios.patch(`/quotes/${quoteId}/status?status=${status}`);
      toast.success("Status updated");
      fetchQuotes();
    } catch {
      toast.error("Failed to update status");
    }
  }, [fetchQuotes]);

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      viewed: "bg-blue-100 text-blue-800",
      contacted: "bg-purple-100 text-purple-800",
      completed: "bg-emerald-100 text-emerald-800",
    };
    return <Badge className={styles[status] || styles.pending}>{status}</Badge>;
  };

  return (
    <div data-testid="quotes-list">
      <h1 className="text-3xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'Outfit' }}>
        Customer Quotes
      </h1>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      ) : quotes.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No quotes yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {quotes.map((quote) => (
            <Card key={quote.id} className="border-slate-200" data-testid={`quote-card-${quote.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg text-slate-900">
                        {quote.customer.name}
                      </h3>
                      {getStatusBadge(quote.status)}
                    </div>
                    <div className="grid sm:grid-cols-3 gap-2 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {quote.customer.email}
                      </span>
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {formatPhoneNumber(quote.customer.phone)}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {quote.customer.address}
                      </span>
                    </div>
                    <div className="mt-3">
                      <p className="text-sm text-slate-500">
                        Services: {quote.selected_services.map((s) => s.service_name).join(", ")}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Total</p>
                      <p className="text-2xl font-bold text-blue-600">${quote.total}</p>
                      {quote.bundle_discount > 0 && (
                        <p className="text-xs text-emerald-600">
                          Saved ${quote.bundle_discount}
                        </p>
                      )}
                    </div>
                    <Select
                      value={quote.status}
                      onValueChange={(val) => updateStatus(quote.id, val)}
                    >
                      <SelectTrigger className="w-32" data-testid={`status-select-${quote.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="viewed">Viewed</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
